import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    ProcessItemRequest,
    ProcessType,
    Rack,
    Item,
} from '../../../types/response'
import { getItem, getRacks, getItems } from '../../../services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Button } from 'vnetwork-ui'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Trash2, Package } from 'lucide-react'

interface SelectedItemsTableProps {
    items: ProcessItemRequest[]
    onRemove: (itemId: string) => void
    onUpdateQuantity: (itemId: string, quantity: number) => void
    processType?: ProcessType
    destinationLocationId?: string
    onUpdateRack?: (itemId: string, rackId: string | undefined) => void
    onUpdatePosition?: (itemId: string, position: number | undefined) => void
    onUpdateParentItemId?: (
        itemId: string,
        parentItemId: string | undefined
    ) => void
}

// Helper to check if process is transfer type
const isTransferType = (type?: ProcessType): boolean =>
    type === 'internal_transfer'

const SelectedItemsTable = ({
    items,
    onRemove,
    onUpdateQuantity,
    processType,
    destinationLocationId,
    onUpdateRack,
    onUpdatePosition,
    onUpdateParentItemId,
}: SelectedItemsTableProps) => {
    const isTransfer = isTransferType(processType)

    // Fetch racks for transfer processes
    const { data: racksResponse, isLoading: isLoadingRacks } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, destinationLocationId],
                queryFn: () =>
                    getRacks({
                        location_id: destinationLocationId,
                        status: 'active',
                    }),
                enabled: isTransfer && Boolean(destinationLocationId),
                staleTime: 30 * 60 * 1000, // 30 minutes - racks rarely change
            },
            { showLoading: false, showError: true }
        )

    const racks = racksResponse?.results || []
    const hasNoRacks =
        isTransfer &&
        !isLoadingRacks &&
        racks.length === 0 &&
        Boolean(destinationLocationId)

    if (items.length === 0) {
        return (
            <div className="border rounded-lg p-8 flex flex-col items-center justify-center text-muted-foreground">
                <Package className="w-12 h-12 mb-3 opacity-50" />
                <div className="font-medium">No items added</div>
                <div className="text-sm mt-1">
                    Click "Add Items" to select items for this process
                </div>
            </div>
        )
    }

    return (
        <div className="border rounded-lg overflow-hidden">
            {hasNoRacks && (
                <div className="px-4 py-2 bg-muted/50 text-sm text-muted-foreground border-b">
                    No active racks at destination. Rack placement is optional.
                </div>
            )}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead
                            className={isTransfer ? 'w-[25%]' : 'w-[40%]'}
                        >
                            Item
                        </TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead className="w-[80px]">Quantity</TableHead>
                        {isTransfer && (
                            <>
                                <TableHead className="w-[140px]">
                                    Dest. Rack
                                </TableHead>
                                <TableHead className="w-[80px]">
                                    Position
                                </TableHead>
                                <TableHead className="w-[160px]">
                                    Parent Equipment
                                </TableHead>
                            </>
                        )}
                        <TableHead className="w-[60px] text-right">
                            Actions
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((item) => (
                        <SelectedItemRow
                            key={item.item_id}
                            item={item}
                            onRemove={onRemove}
                            onUpdateQuantity={onUpdateQuantity}
                            isTransfer={isTransfer}
                            racks={racks}
                            onUpdateRack={onUpdateRack}
                            onUpdatePosition={onUpdatePosition}
                            onUpdateParentItemId={onUpdateParentItemId}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}

interface SelectedItemRowProps {
    item: ProcessItemRequest
    onRemove: (itemId: string) => void
    onUpdateQuantity: (itemId: string, quantity: number) => void
    isTransfer: boolean
    racks: Rack[]
    onUpdateRack?: (itemId: string, rackId: string | undefined) => void
    onUpdatePosition?: (itemId: string, position: number | undefined) => void
    onUpdateParentItemId?: (
        itemId: string,
        parentItemId: string | undefined
    ) => void
}

const SelectedItemRow = ({
    item,
    onRemove,
    onUpdateQuantity,
    isTransfer,
    racks,
    onUpdateRack,
    onUpdatePosition,
    onUpdateParentItemId,
}: SelectedItemRowProps) => {
    // Fetch item details
    const { data: itemDetails, isLoading } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, item.item_id],
            queryFn: () => getItem(item.item_id),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Fetch equipment in the selected rack (only for component items)
    const isComponent = itemDetails?.item_type === 'component'
    const { data: rackEquipmentData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WAREHOUSE_ITEMS,
                'rack-equipment',
                item.rack_id,
            ],
            queryFn: () =>
                getItems({
                    rack_id: item.rack_id,
                    item_type: 'equipment',
                    page_size: 50,
                }),
            enabled: isTransfer && isComponent && Boolean(item.rack_id),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    const rackEquipment: Item[] = rackEquipmentData?.results || []

    const getConditionBadgeVariant = (condition?: string) => {
        switch (condition?.toLowerCase()) {
            case 'new':
                return 'default'
            case 'good':
                return 'secondary'
            case 'fair':
                return 'outline'
            case 'poor':
            case 'damaged':
                return 'destructive'
            default:
                return 'outline'
        }
    }

    // Get selected rack for position max
    const selectedRack = racks.find((r) => r.id === item.rack_id)
    const maxPosition = selectedRack?.u_height || 42

    if (isLoading) {
        return (
            <TableRow>
                <TableCell colSpan={isTransfer ? 8 : 5}>
                    <div className="h-10 animate-pulse bg-muted rounded" />
                </TableCell>
            </TableRow>
        )
    }

    return (
        <TableRow>
            <TableCell>
                <div className="flex flex-col">
                    <span className="font-medium">
                        {itemDetails?.name || item.item_id}
                    </span>
                    {itemDetails && (
                        <>
                            <span className="text-xs text-muted-foreground">
                                {itemDetails.service_tag &&
                                    `ST: ${itemDetails.service_tag}`}
                                {itemDetails.service_tag &&
                                    itemDetails.serial_number &&
                                    ' | '}
                                {itemDetails.serial_number &&
                                    `SN: ${itemDetails.serial_number}`}
                            </span>
                            {/* Current Location & Rack Info */}
                            {(itemDetails.current_location?.name ||
                                itemDetails.rack_position_u) && (
                                <span className="text-xs text-muted-foreground">
                                    {itemDetails.current_location?.name && (
                                        <span>
                                            @{' '}
                                            {itemDetails.current_location.name}
                                        </span>
                                    )}
                                    {itemDetails.rack_position_u && (
                                        <span className="text-blue-600 ml-1">
                                            U{itemDetails.rack_position_u}
                                        </span>
                                    )}
                                </span>
                            )}
                        </>
                    )}
                </div>
            </TableCell>
            <TableCell>{itemDetails?.category || '-'}</TableCell>
            <TableCell>
                <Badge
                    variant={getConditionBadgeVariant(
                        item.condition || itemDetails?.condition
                    )}
                >
                    {item.condition || itemDetails?.condition || 'Unknown'}
                </Badge>
            </TableCell>
            <TableCell>
                <Input
                    type="number"
                    min={1}
                    value={item.quantity || 1}
                    onChange={(e) =>
                        onUpdateQuantity(
                            item.item_id,
                            Math.max(1, parseInt(e.target.value) || 1)
                        )
                    }
                    className="w-16 h-8"
                />
            </TableCell>
            {isTransfer && (
                <>
                    <TableCell>
                        <Select
                            value={item.rack_id || '__none__'}
                            onValueChange={(value) =>
                                onUpdateRack?.(
                                    item.item_id,
                                    value === '__none__' ? undefined : value
                                )
                            }
                        >
                            <SelectTrigger className="h-8 w-[130px]">
                                <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {racks.map((rack) => (
                                    <SelectItem key={rack.id} value={rack.id}>
                                        {rack.code} ({rack.u_height}U)
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </TableCell>
                    <TableCell>
                        {isComponent && item.parent_item_id ? (
                            <span className="text-xs text-muted-foreground">
                                —
                            </span>
                        ) : (
                            <Input
                                type="number"
                                min={1}
                                max={maxPosition}
                                value={item.position || ''}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value)
                                    onUpdatePosition?.(
                                        item.item_id,
                                        isNaN(val)
                                            ? undefined
                                            : Math.min(
                                                  Math.max(1, val),
                                                  maxPosition
                                              )
                                    )
                                }}
                                placeholder="U"
                                disabled={!item.rack_id}
                                className="w-16 h-8"
                            />
                        )}
                    </TableCell>
                    <TableCell>
                        {isComponent && item.rack_id ? (
                            <Select
                                value={item.parent_item_id || '__none__'}
                                onValueChange={(value) =>
                                    onUpdateParentItemId?.(
                                        item.item_id,
                                        value === '__none__' ? undefined : value
                                    )
                                }
                            >
                                <SelectTrigger className="h-8 w-[150px]">
                                    <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">
                                        None
                                    </SelectItem>
                                    {rackEquipment.map((eq) => (
                                        <SelectItem key={eq.id} value={eq.id}>
                                            {eq.name}
                                            {eq.rack_position_u
                                                ? ` (U${eq.rack_position_u})`
                                                : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <span className="text-xs text-muted-foreground">
                                {!isComponent ? '—' : 'Select rack first'}
                            </span>
                        )}
                    </TableCell>
                </>
            )}
            <TableCell className="text-right">
                <Button
                    variant="text"
                    type="button"
                    onClick={() => onRemove(item.item_id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            </TableCell>
        </TableRow>
    )
}

export default SelectedItemsTable
